import { RefObject, useCallback, useEffect, useState } from "react";

import { FACILITY_PAINT_LAYERS, WELL_PAINT_LAYERS } from "constants/mapLayers.constants";

import { EntityKind } from "models/entityKind";

import { EntitiesInGroupModel } from "../../contexts/MapContextState";
import {
  FrameRect,
  areFrameCountsEqual,
  countWellsInFrame,
  isCountedSource,
  isFrameCountPending
} from "../utils/countWellsInFrame";

export interface UseScreenshotFrameCountsParams {
  readonly mapbox: Maybe<mapboxgl.Map>;
  readonly entityKind: EntityKind;
  /** True while the capture frame is on screen. */
  readonly isComposing: boolean;
  /**
   * The capture frame element — the same one `copyMapToClipboard` crops to, so
   * the counts describe exactly the pixels that end up in the image.
   */
  readonly frameRef: Maybe<RefObject<HTMLElement>>;
  /** `entitiesInGroup` or `facilitiesInGroup` — the map's own group → entities map. */
  readonly entitiesByGroup: Maybe<Record<string, EntitiesInGroupModel>>;
}

/**
 * The capture frame's rectangle, in the canvas-relative CSS pixels that
 * `queryRenderedFeatures` and `project` work in. Both rects are page-relative,
 * so subtracting the canvas origin is what makes the two comparable.
 */
export const readFrameRect = (
  mapbox: mapboxgl.Map,
  frameElement: Maybe<Element>
): Nullable<FrameRect> => {
  if (!frameElement) {
    return null;
  }
  const frame = frameElement.getBoundingClientRect();
  const canvas = mapbox.getCanvas().getBoundingClientRect();
  return {
    left: frame.left - canvas.left,
    top: frame.top - canvas.top,
    right: frame.right - canvas.left,
    bottom: frame.bottom - canvas.top
  };
};

/**
 * How many of the entities the map is drawing fall inside the capture frame,
 * per legend group — so the legend printed on a screenshot describes the
 * screenshot while the map keeps showing everything around it.
 *
 * Recounted as soon as a counted source finishes loading — so the legend
 * follows the wells rather than the slowest layer on the map — plus when the
 * camera settles at `moveend`, and again at `idle` as the final backstop.
 * Nothing is fetched and nothing re-derives: bins, colors and titles stay the
 * live legend's. Null whenever the frame cannot be counted and there is no
 * earlier count to hold, so the caller shows the live counts as-is.
 */
export function useScreenshotFrameCounts({
  mapbox,
  entityKind,
  isComposing,
  frameRef,
  entitiesByGroup
}: UseScreenshotFrameCountsParams): Nullable<Record<string, number>> {
  const [countsByGroup, setCountsByGroup] =
    useState<Nullable<Record<string, number>>>(null);

  const layerIds =
    entityKind === EntityKind.Facility ? FACILITY_PAINT_LAYERS : WELL_PAINT_LAYERS;

  const recount = useCallback(() => {
    if (!mapbox) {
      return;
    }
    const frame = readFrameRect(mapbox, frameRef?.current);
    if (!frame) {
      setCountsByGroup(null);
      return;
    }
    const nextCounts = countWellsInFrame({ mapbox, frame, layerIds, entitiesByGroup });
    // Every refilter — entry widening the viewport, every pan — changes the
    // well set and reloads its tiles, and for that second the frame cannot be
    // counted. Dropping to null would print whole-map counts on every row
    // until `idle`; the previous frame counts are one refilter behind but
    // frame-scoped, which is the better number to be showing. Null still wins
    // when there is nothing to hold, or when the frame is uncountable outright.
    const shouldHoldPrevious =
      nextCounts === null && isFrameCountPending(mapbox, layerIds, entitiesByGroup);
    setCountsByGroup((previousCounts) => {
      if (shouldHoldPrevious && previousCounts) {
        return previousCounts;
      }
      // Same values keep the same identity, so React can bail out of the
      // re-render a fresh-but-equal object would force on every recount.
      return areFrameCountsEqual(previousCounts, nextCounts)
        ? previousCounts
        : nextCounts;
    });
  }, [mapbox, frameRef, layerIds, entitiesByGroup]);

  useEffect(() => {
    if (!mapbox || !isComposing) {
      setCountsByGroup(null);
      return;
    }
    // The frame is in the DOM by the time this effect runs, but mapbox has not
    // yet re-rendered for the canvas the screenshot overlay resized: a
    // synchronous query would read the previous frame's rendering against the
    // new frame rectangle. The next animation frame is the earliest settled
    // point — and mapbox emits no further `idle` when the map is already
    // settled, so waiting for one would leave the legend on live counts
    // indefinitely.
    const firstCount = requestAnimationFrame(recount);
    // Every trigger below funnels into one queued frame. The one-frame defer
    // coalesces bursts, and is also load-bearing on the `sourcedata` path: on a
    // refilter that event can say "loaded" over zero tiles (the reload
    // announces itself before the tile cache is cleared), and a synchronous
    // count would read all-zeros — non-null, so straight past the hold guard,
    // and the zero-count filter would then drop the whole legend from the
    // screenshot. Safe by construction: mapbox's own `data` handler runs
    // `_update()` — queueing mapbox's render frame ahead of this one and
    // re-dirtying the source — *before* re-firing the event as `sourcedata`,
    // so the queued recount sees the source as still loading and the hold
    // takes over.
    let queuedRecountFrame: Nullable<number> = null;
    let recountAgainAfterQueued = false;
    const scheduleRecount = () => {
      // A trigger landing while a recount is queued must not be dropped: it
      // can be the *final* "source loaded" of a reload, and the queued recount
      // may still read the mid-reload state and hold — with nothing left to
      // re-trigger until `idle`, which a slow unrelated source can stall for
      // as long as it likes. One follow-up run per window keeps coalescing
      // without losing that event.
      if (queuedRecountFrame !== null) {
        recountAgainAfterQueued = true;
        return;
      }
      queuedRecountFrame = requestAnimationFrame(() => {
        queuedRecountFrame = null;
        recount();
        if (recountAgainAfterQueued) {
          recountAgainAfterQueued = false;
          scheduleRecount();
        }
      });
    };
    // Editing width/height/scale resizes the frame without moving the map, so
    // no `idle` arrives and the counts would keep describing the old rectangle.
    // A frame-edge drag fires this per tick, which is what the queue is for.
    const frameElement = frameRef?.current;
    const frameObserver =
      frameElement && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => scheduleRecount())
        : null;
    if (frameElement && frameObserver) {
      frameObserver.observe(frameElement);
    }
    // `idle` is the backstop: it fires once the camera has settled and every
    // source has loaded, correcting any count taken while tiles were arriving.
    mapbox.on("idle", scheduleRecount);
    // `idle` also waits for every *other* source — base map, grids, IPDB — so
    // a slow one keeps the legend on held or live counts for seconds after the
    // wells are already painted. A counted source announcing its own completion
    // is the earliest the frame is countable; recounting then means the legend
    // follows the wells, not the slowest layer on the map.
    const recountOnceSourceLands = (event: mapboxgl.MapSourceDataEvent) => {
      // Mid-pan the number cannot hold still, and the counted source reports
      // "loaded" once per tile batch — a repaint per batch, each a frame
      // behind the camera. `moveend` below picks the count up the moment the
      // camera settles instead.
      if (mapbox.isMoving?.()) {
        return;
      }
      if (!event.isSourceLoaded || !isCountedSource(mapbox, layerIds, event.sourceId)) {
        return;
      }
      scheduleRecount();
    };
    mapbox.on("sourcedata", recountOnceSourceLands);
    // The settling edge the mid-pan skip needs: the last well tiles can land
    // while the camera is still moving — skipped above — and if an unrelated
    // source hangs, no `idle` follows to catch up. Counting at `moveend` is
    // safe where it once was not: tiles still arriving read as pending and the
    // previous counts hold until a later `sourcedata` lands them.
    mapbox.on("moveend", scheduleRecount);
    // Entities can appear without any tile load: the id filter lands on
    // already-loaded tiles via `setFilter` (how facilities show up once their
    // entity list arrives), and the timeline toggles layer visibility — both
    // style-only changes that emit `styledata`, never `sourcedata`. Without
    // this trigger those repaints go uncounted until `idle`. Style mutations
    // can land mid-gesture too (label density, highlight filters), and a
    // mid-pan count would record a frame the user never composed — the same
    // skip as `sourcedata`, with `moveend` as the same backstop.
    const recountOnceStyleSettles = () => {
      if (mapbox.isMoving?.()) {
        return;
      }
      scheduleRecount();
    };
    mapbox.on("styledata", recountOnceStyleSettles);
    return () => {
      cancelAnimationFrame(firstCount);
      if (queuedRecountFrame !== null) {
        cancelAnimationFrame(queuedRecountFrame);
      }
      frameObserver?.disconnect();
      mapbox.off("idle", scheduleRecount);
      mapbox.off("sourcedata", recountOnceSourceLands);
      mapbox.off("moveend", scheduleRecount);
      mapbox.off("styledata", recountOnceStyleSettles);
    };
  }, [mapbox, isComposing, frameRef, layerIds, recount]);

  return isComposing ? countsByGroup : null;
}
